/** The user detail panel: everything support needs about one account, plus the
 * actions staff are allowed to take on it.
 *
 * Destructive actions arm before they fire (click once to arm, again within the
 * panel to confirm) rather than opening a browser dialog — a native `confirm()`
 * blocks the whole page and reads as a crash on a slow connection.
 */

import { useEffect, useState } from 'react'
import type { Tier } from '../../lib/api'
import { adminApi, type AdminUserDetail } from '../../lib/adminApi'
import { DataTable, ErrorNote, Loading, Pill, ago, dateTime, fmt, useAsync } from './parts'

const TIERS: Tier[] = ['free', 'pro', 'family']

export function UserPanel({
  userId,
  isSuperAdmin,
  selfId,
  onClose,
  onChanged,
}: {
  userId: string
  isSuperAdmin: boolean
  selfId: string
  onClose: () => void
  onChanged: () => void
}) {
  const { data, error, loading, reload } = useAsync(
    () => adminApi.user(userId),
    `user:${userId}`,
  )
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [draftNote, setDraftNote] = useState('')
  const [reason, setReason] = useState('')
  const [password, setPassword] = useState('')
  const [armed, setArmed] = useState<'delete' | null>(null)

  useEffect(() => {
    setDraftNote(data?.admin_note ?? '')
    setReason(data?.suspended_reason ?? '')
    setArmed(null)
  }, [data])

  async function run(what: string, fn: () => Promise<unknown>, refresh = true) {
    setBusy(true)
    setFailure(null)
    setNote(null)
    try {
      await fn()
      setNote(what)
      if (refresh) reload()
      onChanged()
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (error) return <ErrorNote message={error} />
  if (!data) return <Loading what="account" />

  const u: AdminUserDetail = data
  const isSelf = u.id === selfId
  const suspended = u.suspended_at !== null

  return (
    <aside className="staff-panel glass" aria-label={`Account ${u.email}`}>
      <header className="staff-panel__head">
        <div>
          <strong>{u.display_name}</strong>
          <span>{u.email}</span>
        </div>
        <button className="icon-btn" onClick={onClose} aria-label="Close panel">
          ✕
        </button>
      </header>

      <div className="staff-panel__pills">
        <Pill tone={u.tier === 'free' ? 'slate' : 'teal'}>{u.tier}</Pill>
        {u.is_super_admin && <Pill tone="amber">super admin</Pill>}
        {u.is_admin && !u.is_super_admin && <Pill tone="amber">admin</Pill>}
        {suspended && <Pill tone="red">suspended</Pill>}
        {isSelf && <Pill tone="teal">you</Pill>}
      </div>

      {failure && <ErrorNote message={failure} />}
      {note && (
        <p className="banner" role="status">
          {note}
        </p>
      )}

      <dl className="staff-facts">
        <div>
          <dt>Joined</dt>
          <dd>{dateTime(u.created_at)}</dd>
        </div>
        <div>
          <dt>Last seen</dt>
          <dd>{ago(u.last_seen_at)}</dd>
        </div>
        <div>
          <dt>Reminders</dt>
          <dd>
            {fmt(u.reminders_active)} active / {fmt(u.reminders_total)} total
          </dd>
        </div>
        <div>
          <dt>Households</dt>
          <dd>{fmt(u.household_count)}</dd>
        </div>
        <div>
          <dt>Push devices</dt>
          <dd>{fmt(u.push_sub_count)}</dd>
        </div>
        <div>
          <dt>Default radius</dt>
          <dd>{(u.default_radius_m / 1609.344).toFixed(2)} mi</dd>
        </div>
        <div>
          <dt>Last position</dt>
          <dd>
            {u.last_position
              ? `${u.last_position.lat.toFixed(4)}, ${u.last_position.lon.toFixed(4)} · ${ago(
                  u.last_position.reported_at,
                )}`
              : 'none stored'}
          </dd>
        </div>
      </dl>

      <h3 className="staff-panel__h">Plan</h3>
      <div className="staff-seg" role="group" aria-label="Plan">
        {TIERS.map((t) => (
          <button
            key={t}
            className="staff-seg__btn"
            aria-pressed={u.tier === t}
            disabled={busy || u.tier === t}
            onClick={() => void run(`Moved to ${t}.`, () => adminApi.patchUser(u.id, { tier: t }))}
          >
            {t}
          </button>
        ))}
      </div>

      <h3 className="staff-panel__h">Access</h3>
      {isSuperAdmin ? (
        <div className="staff-actions">
          <button
            className="btn btn--ghost btn--sm"
            disabled={busy || isSelf}
            title={isSelf ? 'You cannot change your own access' : undefined}
            onClick={() =>
              void run(
                u.is_admin ? 'Admin access revoked.' : 'Admin access granted.',
                () => adminApi.patchUser(u.id, { is_admin: !u.is_admin }),
              )
            }
          >
            {u.is_admin ? 'Revoke admin' : 'Make admin'}
          </button>
          <button
            className="btn btn--ghost btn--sm"
            disabled={busy || isSelf}
            onClick={() =>
              void run(
                u.is_super_admin ? 'Super admin revoked.' : 'Promoted to super admin.',
                () => adminApi.patchUser(u.id, { is_super_admin: !u.is_super_admin }),
              )
            }
          >
            {u.is_super_admin ? 'Revoke super admin' : 'Make super admin'}
          </button>
        </div>
      ) : (
        <p className="hint">Only a super admin can change staff access.</p>
      )}

      <h3 className="staff-panel__h">Suspension</h3>
      {suspended ? (
        <>
          <p className="hint">
            Suspended {ago(u.suspended_at)} — {u.suspended_reason ?? 'no reason recorded'}. They
            see a message instead of the app; nothing has been deleted.
          </p>
          <button
            className="btn btn--ghost btn--sm"
            disabled={busy}
            onClick={() =>
              void run('Access restored.', () =>
                adminApi.patchUser(u.id, { suspended: false }),
              )
            }
          >
            Restore access
          </button>
        </>
      ) : (
        <div className="staff-actions">
          <input
            className="staff-input"
            placeholder="Reason (shown to them)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <button
            className="btn btn--ghost btn--sm"
            disabled={busy || isSelf}
            onClick={() =>
              void run('Account suspended.', () =>
                adminApi.patchUser(u.id, {
                  suspended: true,
                  suspended_reason: reason || undefined,
                }),
              )
            }
          >
            Suspend
          </button>
        </div>
      )}

      <h3 className="staff-panel__h">Staff note</h3>
      <textarea
        className="staff-input staff-input--area"
        rows={3}
        value={draftNote}
        placeholder="Context for whoever picks this up next. Never shown to the user."
        onChange={(e) => setDraftNote(e.target.value)}
      />
      <button
        className="btn btn--ghost btn--sm"
        disabled={busy || draftNote === (u.admin_note ?? '')}
        onClick={() =>
          void run('Note saved.', () => adminApi.patchUser(u.id, { admin_note: draftNote }))
        }
      >
        Save note
      </button>

      {isSuperAdmin && (
        <>
          <h3 className="staff-panel__h">Recovery</h3>
          <p className="hint">
            There is no password-reset email in this build, so a locked-out account is
            recovered by setting a temporary password and handing it over.
          </p>
          <div className="staff-actions">
            <input
              className="staff-input"
              type="text"
              autoComplete="off"
              placeholder="New password (min 8)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              className="btn btn--ghost btn--sm"
              disabled={busy || password.length < 8}
              onClick={() =>
                void run('Password set.', async () => {
                  await adminApi.setPassword(u.id, password)
                  setPassword('')
                })
              }
            >
              Set password
            </button>
          </div>
        </>
      )}

      <h3 className="staff-panel__h">Privacy</h3>
      <button
        className="btn btn--ghost btn--sm"
        disabled={busy || !u.last_position}
        onClick={() =>
          void run('Stored position cleared.', () => adminApi.clearPosition(u.id))
        }
      >
        Forget last known location
      </button>

      {isSuperAdmin && !isSelf && (
        <>
          <h3 className="staff-panel__h staff-panel__h--danger">Delete account</h3>
          <p className="hint">
            Removes the account, its reminders, categories, push devices and position. The
            audit trail keeps a record of the deletion.
          </p>
          {armed === 'delete' ? (
            <div className="staff-actions">
              <button
                className="btn btn--ghost btn--sm staff-danger"
                disabled={busy}
                onClick={() =>
                  void run(
                    'Account deleted.',
                    async () => {
                      await adminApi.deleteUser(u.id)
                      onClose()
                    },
                    false,
                  )
                }
              >
                Yes, delete {u.email}
              </button>
              <button className="btn btn--ghost btn--sm" onClick={() => setArmed(null)}>
                Cancel
              </button>
            </div>
          ) : (
            <button
              className="btn btn--ghost btn--sm staff-danger"
              disabled={busy}
              onClick={() => setArmed('delete')}
            >
              Delete account
            </button>
          )}
        </>
      )}

      <h3 className="staff-panel__h">Reminders</h3>
      <DataTable
        rows={u.reminders}
        rowId={(r) => r.id}
        empty="No reminders."
        columns={[
          { key: 't', head: 'Title', render: (r) => r.title },
          { key: 'm', head: 'Binds to', render: (r) => r.merchant_label },
          {
            key: 's',
            head: 'State',
            render: (r) =>
              r.completed_at ? (
                <Pill tone="green">done</Pill>
              ) : r.active ? (
                <Pill tone="teal">active</Pill>
              ) : (
                <Pill>paused</Pill>
              ),
          },
          { key: 'n', head: 'Nudges', numeric: true, render: (r) => fmt(r.trigger_count) },
        ]}
      />

      <h3 className="staff-panel__h">Recent nudges</h3>
      <DataTable
        rows={u.recent_triggers}
        rowId={(r) => r.id}
        empty="Nothing has fired for this account."
        columns={[
          { key: 'w', head: 'When', render: (r) => dateTime(r.fired_at) },
          { key: 'r', head: 'Reminder', render: (r) => r.reminder_title ?? '—' },
          { key: 's', head: 'Store', render: (r) => r.store_name ?? '—' },
          {
            key: 'o',
            head: 'Fence',
            render: (r) => (r.cleared_at ? 'cleared' : <Pill tone="amber">inside</Pill>),
          },
        ]}
      />

      {loading && <p className="staff-note">Refreshing…</p>}
    </aside>
  )
}
